'use client'

import React from 'react'
import { useEffect, useState, useContext } from 'react'
import { RootContext } from '@/modules/contextService'
import { useSearchParams, useRouter } from 'next/navigation'
import { PATHWAY } from '@/modules/httpService'
import MuiButton from '@mui/material/Button'
import { useInView } from 'react-intersection-observer'
import { IFeedRecord } from '@/interfaces/IFeedRecord'
import { IFeedView } from '@/interfaces/IFeedView'
import FeedRow2 from '@/components/display/FeedRow2'
import FeedRowShimmer from '@/components/display/FeedRowShimmer'
import FeedSidebar from '@/components/display/FeedSidebar'
import { ITag } from '@/interfaces/ITag'
import { MINISTRY } from '@/modules/stringService'
// import FeedRowUserInvite from '@/components/advertisements/FeedRowUserInvite'
import styles from './profile.module.scss'
import SubmitReportModal from '@/components/inputs/SubmitReportModal'
import FeedNav from '@/components/inputs/FeedNav'
import { useTranslations } from 'next-intl'
import themeService from '@/modules/themeService'

interface PageParams {
  params: {
    slug?: string
  }
}

function Feed ({ params }: PageParams) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { rootData } = useContext(RootContext)
  const [initialLoading, setInitialLoading] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingView, setIsLoadingView] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [records, setRecords] = useState<IFeedRecord[]>([])
  const [lastTimestamp, setLastTimestamp] = useState<string | null>(null)
  const [loadComplete, setLoadComplete] = useState<boolean>(false)
  const { ref, inView } = useInView({ threshold: 0 })
  const [view, setView] = useState<IFeedView | null>(null)
  const [selectedTag, setSelectedTag] = useState<ITag | null>(null)
  const [mainFilter, setMainFilter] = useState<{ type: string, value: string } | null>(null)
  const [postCount, setPostCount] = useState<number>(0)
  const [reportModalId, setReportModalId] = useState<string | null>(null)
  const [sort, setSort] = useState<'newest' | 'liked' | 'read'>('newest')
  const [since, setSince] = useState<'week' | 'month' | null>(null)
  const [page, setPage] = useState<number>(1)

  const count = records.length
  // const { user } = rootData

  interface ILoadRecords {
    tab?: string
    topic?: string
  }

  const resolveCategoryId = (tab?: string): string | null => {
    if (!tab || !view) return null
    const categories = view.categories.sort((a, b) => a.index - b.index)
    const match = categories.find(cat => cat.name.toLowerCase().replaceAll(' ', '-') === tab)
    return match ? match.id : null
  }

  /**
   * will load the records based on the tab or topic query params
   *
   * if no query params are provided, it will pull from cache if available and valid
   * it will then silently query the server for new records and overwrite the cache
   * this makes sure that the user gets a fast response and the cache is updated in the background
   */
  async function loadRecords ({ tab, topic }: ILoadRecords): Promise<void> {
    // liked sort always uses the public feed; newest uses self/feed for logged-in users
    const useSelfFeed = !tab && sort === 'newest' && rootData.user
    let url = `${useSelfFeed ? '/self' : ''}/feed`
    if (!rootData.user) url = '/feed'

    const query: {
      category?: string,
      categoryId?: string,
      tagId?: string,
      search?: string,
      sort?: string,
      since?: string,
      count?: string
    } = {}

    const queryString = searchParams?.get('q')

    if (tab) {
      const categoryId = resolveCategoryId(tab)
      if (categoryId) query.categoryId = categoryId
      else query.category = tab
    }
    if (topic) query.tagId = topic
    if (queryString) query.search = queryString
    if (sort !== 'newest') query.sort = sort
    if (sort !== 'newest' && since) query.since = since

    const hasFilters = Object.keys(query).length > 0
    if (hasFilters) {
      url += `?${new URLSearchParams(query).toString()}&count=true`
      if (params.slug) url += `&lang=${params.slug}`
    } else {
      if (params.slug) url += `?lang=${params.slug}`
    }

    // only cache the default (newest, no filters) feed in localStorage
    const isCacheable = sort === 'newest' && !since && Object.keys(query).length === 0
    if (isCacheable) {
      const cachedFeed = localStorage.getItem('cache:feed')
      /**
       * if cache hit, set the records and pagination from the cache
       */
      if (cachedFeed) {
        // check if the cache is still valid
        const cache = JSON.parse(cachedFeed)

        if (cache.exp > Date.now()) {
          if (cache.feed.length > 0) setLastTimestamp(cache.feed[cache.feed.length - 1].publishedAt)
          setPostCount(cache.pagination.totalCount)
          setRecords(cache.feed)
          setInitialLoading(false)
          setIsLoading(false)
        }
      }
    }

    const res = await PATHWAY.get(url)
    const feed = res.data.data.feed

    if (feed.length > 0) setLastTimestamp(feed[feed.length - 1].publishedAt)
    setPostCount(res.data.pagination.totalCount)
    setRecords(feed)
    setInitialLoading(false)
    setIsLoading(false)

    if (isCacheable) {
      localStorage.setItem('cache:feed', JSON.stringify({
        exp: Date.now() + 1000 * 60 * 60 * 9, // expires in 9 hours
        feed: res.data.data.feed,
        pagination: res.data.pagination
      }))
    }
  }

  async function loadMore (): Promise<void> {
    if (loadComplete) return undefined

    let url: string
    if (sort !== 'newest') {
      // page-based pagination for engagement sorts (liked, read)
      const nextPage = page + 1
      url = `/feed?sort=liked&page=${nextPage}`
      if (since) url += `&since=${since}`
      if (mainFilter) {
        if (mainFilter.type === 'topic') url += `&tagId=${mainFilter.value}`
        else {
          const categoryId = resolveCategoryId(mainFilter.value)
          url += categoryId ? `&categoryId=${categoryId}` : `&category=${mainFilter.value}`
        }
      }
      if (params.slug) url += `&lang=${params.slug}`
      const res = await PATHWAY.get(url)
      const feed = res.data.data.feed
      if (feed.length < 15) setLoadComplete(true)
      setPage(nextPage)
      setRecords(records.concat(feed))
      setIsLoadingMore(false)
      return
    }

    // cursor-based pagination for newest sort
    url = `/self/feed?lastTimestamp=${lastTimestamp}`
    if (!rootData.user) url = `/feed?lastTimestamp=${lastTimestamp}`
    if (mainFilter) {
      if (mainFilter.type === 'topic') {
        url += `&tagId=${mainFilter.value}`
      } else {
        const categoryId = resolveCategoryId(mainFilter.value)
        url += categoryId ? `&categoryId=${categoryId}` : `&category=${mainFilter.value}`
      }
    }
    if (params.slug) url += `&lang=${params.slug}`
    const res = await PATHWAY.get(url)
    const feed = res.data.data.feed

    if (feed.length > 0) {
      setLastTimestamp(feed[feed.length - 1].publishedAt)
    }

    if (feed.length < 15) setLoadComplete(true)

    setRecords(records.concat(feed))
    setIsLoadingMore(false)
  }

  async function loadView (): Promise<void> {
    const cachedView = localStorage.getItem('cache:feed-view')
    if (cachedView) {
      const cache = JSON.parse(cachedView)

      if (cache.exp > Date.now()) {
        setView({
          accounts: cache.accounts,
          categories: cache.categories,
          tags: cache.tags
        })
        setIsLoadingView(false)
      }
    }

    const res = await PATHWAY.get('/views/feed-view?primary=true')
    res.data.data.tags = res.data.data.tags.sort((a: ITag, b: ITag) => b.name.localeCompare(a.name))
    const v = res.data.data
    setView({ accounts: v.accounts, categories: v.categories, tags: v.tags })
    setIsLoadingView(false)

    localStorage.setItem('cache:feed-view', JSON.stringify({
      exp: Date.now() + 1000 * 60 * 60 * 9, // expires in 9 hours
      accounts: v.accounts,
      categories: v.categories,
      tags: v.tags
    }))
  }

  async function loadTag (tagId: string): Promise<void> {
    const res = await PATHWAY.get(`/tags/${tagId}`)
    setSelectedTag(res.data.data.tags[0])
    setPostCount(0)
  }

  /**
   * ! this will run when the ref is in view
   */
  useEffect(() => {
    if (inView && !isLoadingMore && !loadComplete) {
      setIsLoadingMore(true)
      loadMore()
    }
  }, [inView, isLoadingMore, loadComplete])

  /**
   * ! this will run when the mainFilter state changes
   */
  useEffect(() => {
    if (!mainFilter && !initialLoading && rootData.loading === false) {
      loadRecords({})
      loadView()
      setSelectedTag(null)
    }
  }, [mainFilter])

  /**
   * ! this will run on initial page load
   */
  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.title = MINISTRY
    }
    loadView()
    const tab = searchParams?.get('tab')
    const topic = searchParams?.get('topic')
    const sortParam = searchParams?.get('sort') as 'newest' | 'liked' | 'read' | null
    const sinceParam = searchParams?.get('since') as 'week' | 'month' | null
    if (sortParam && sortParam !== sort) setSort(sortParam)
    if (sinceParam && sinceParam !== since) setSince(sinceParam)

    if (!tab && !topic && rootData.loading === false) {
      loadRecords({})
    }
  }, [rootData.loading === false])

  /**
   * ! this will run when sort or since changes
   */
  useEffect(() => {
    if (initialLoading) return
    setIsLoading(true)
    setRecords([])
    setLastTimestamp(null)
    setPage(1)
    setLoadComplete(false)
    const tab = searchParams?.get('tab') || undefined
    const topic = searchParams?.get('topic') || undefined
    loadRecords({ tab, topic })
    // update URL to reflect sort/since state
    const params = new URLSearchParams(searchParams?.toString() || '')
    if (sort !== 'newest') params.set('sort', sort)
    else params.delete('sort')
    if (sort !== 'newest' && since) params.set('since', since)
    else params.delete('since')
    router.replace(`?${params.toString()}`, { scroll: false })
  }, [sort, since])

  /**
   * ! this will run when the tab or topic query params change
   */
  useEffect(() => {
    const tab = searchParams?.get('tab')
    const topic = searchParams?.get('topic')

    if (tab) {
      setMainFilter({ type: 'tab', value: tab })
      setIsLoading(true)
      setLastTimestamp(null)
      setRecords([])
      loadRecords({ tab })
      setSelectedTag(null)
    } else if (topic) {
      setMainFilter({ type: 'topic', value: topic })
      setIsLoading(true)
      setRecords([])
      setLastTimestamp(null)
      loadRecords({ topic })
      loadTag(topic)
    } else {
      setMainFilter(null)
    }
  }, [searchParams])

  useEffect(() => {
    /**
     * detect what color theme is being used on html[data-theme] css
     * then compare it with whatever is stored in cookies, if they are not the same then refresh the page
     */
    const getCurrentTheme = () => document.documentElement.getAttribute('data-theme')
    const getStoredTheme = () => themeService.clientGetTheme()

    const currentTheme = getCurrentTheme()
    const storedTheme = getStoredTheme()

    // Compare the themes and refresh the page if they are not the same
    if (currentTheme !== storedTheme) {
    // Refresh the page
      console.log('not the same theme, reloading page')
      window.location.reload()
    }

  }, [])

  const t = useTranslations('FeedPage')

  if (records.length === 0 && (isLoading || isLoadingView)) {
    return (
      <div className={styles.loadWrapper}>
        <div className={styles.loader}>
          <FeedRowShimmer count={5} />
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={styles.outerWrapper}>
        <div className={styles.wrapper}>
          <FeedNav
            mainFilter={mainFilter}
            view={view}
            sort={sort}
            since={since}
            onSortChange={(s) => {
              if (s === 'newest') setSince(null)
              setSort(s)
            }}
            onSinceChange={setSince}
            forYouClicked={() => {
              setIsLoading(true)
              setLastTimestamp(null)
              setPage(1)
              setRecords([])
              loadRecords({})
              setSelectedTag(null)
              router.replace('/feed', { scroll: false })
            }}
          />
          {selectedTag &&
            <div className={styles.titleBar}>
              <h1 className={styles.tagTitle}>{selectedTag.name}</h1>
              <div className={styles.tagMinor}>
                <div className={styles.left}>Topic</div>
                <div className={styles.dot}>&middot;</div>
                <div className={styles.right}>{postCount} post{postCount === 1 ? '' : 's'}</div>
              </div>
            </div>
          }
          {records.map((record, i) => {
            /**
             * this will trigger the ref to be added and control if the loadMore function should be called
             */
            const shouldAddRef = (count >= 5 ? count - 5 : count) === i

            return (
              <FeedRow2
                key={`${i}-${record.id}`}
                record={record}
                ref={shouldAddRef ? ref : undefined}
                selectedTag={selectedTag}
                openReportDialog={(id: string) => {
                  // open the report dialog
                  setReportModalId(id)
                }}
              />
            )
          })}
          {!isLoadingMore && !isLoading && !loadComplete && count > 0 &&
            <MuiButton
              variant='outlined'
              fullWidth
              color='secondary'
              className={styles.button}
              onClick={async () => {
                setIsLoadingMore(true)
                await loadMore()
              }}
            >
              {t('load-more')}
            </MuiButton>
          }
          {records.length === 0 && !isLoadingMore && !isLoading && !loadComplete &&
            <div className={styles.noPosts} key={'no-posts'}>
              <h3>{t('no-more-posts')}</h3>
            </div>
          }
        </div>
        {view &&
          <FeedSidebar
            tags={view.tags.slice(0, 5)}
            accounts={view.accounts.slice(0, 5)}
            isLoggedIn={rootData.user?.id ? true : false}
          />
        }
      </div>
      <SubmitReportModal
        reportModalId={reportModalId}
        setReportModalId={setReportModalId}
        isLoggedIn={rootData.user?.id ? true : false}
      />
    </>
  )
}

export default Feed
